import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Coins, ArrowDownLeft, ArrowUpRight, Info } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WalletPage() {
  const { t } = useLang();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [wr, tr] = await Promise.all([
          axios.get(`${API}/wallet`, { withCredentials: true }),
          axios.get(`${API}/wallet/transactions`, { withCredentials: true })
        ]);
        setWallet(wr.data);
        setTransactions(tr.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  const balance = wallet?.balance || 0;
  // EARN types: 'earn' (legacy), 'REVIEW_AUTHOR_REWARD' (1%), 'REFERRER_REWARD' (4%), 'refund'
  const EARN_TYPES = new Set(['earn', 'REVIEW_AUTHOR_REWARD', 'REFERRER_REWARD', 'refund']);
  const isEarn = (t) => EARN_TYPES.has(t);
  const totalEarned = transactions.filter(t => isEarn(t.type) && t.type !== 'refund').reduce((s, t) => s + t.amount, 0);
  const totalSpent = transactions.filter(t => t.type === 'used' || t.type === 'COIN_DISCOUNT').reduce((s, t) => s + t.amount, 0);

  const txnBadge = (type) => {
    if (isEarn(type)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (type === 'used' || type === 'COIN_DISCOUNT') return 'bg-primary/10 text-primary border-primary/20';
    if (type === 'expired') return 'bg-muted text-muted-foreground border-border';
    return 'bg-muted text-muted-foreground border-border';
  };

  const txnLabel = (type) => {
    if (type === 'REVIEW_AUTHOR_REWARD') return 'Reward recensione (1%)';
    if (type === 'REFERRER_REWARD') return 'Reward referral (4%)';
    if (type === 'COIN_DISCOUNT' || type === 'used') return 'Sconto usato';
    if (type === 'earn') return 'Guadagnato';
    if (type === 'refund') return 'Rimborso';
    if (type === 'expired') return 'Scaduto';
    return type;
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="wallet-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('nav_wallet')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('w_title')}</h1>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="border border-border rounded-2xl bg-gradient-to-br from-card to-secondary/30" data-testid="wallet-balance">
            <CardContent className="p-5 sm:p-6 text-center">
              <Coins className="w-8 sm:w-10 h-8 sm:h-10 text-primary mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1 font-sans">{t('w_balance')}</p>
              <p className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{balance.toFixed(2)}</p>
              <p className="text-muted-foreground text-xs mt-1 font-sans">{t('w_available')}</p>
            </CardContent>
          </Card>
          <Card className="border border-border rounded-2xl" data-testid="total-earned-card">
            <CardContent className="p-5 sm:p-6 text-center">
              <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-2"><ArrowDownLeft className="w-5 h-5 text-emerald-700" strokeWidth={1.5} /></div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1 font-sans">{t('w_earned')}</p>
              <p className="text-2xl sm:text-3xl font-bold text-emerald-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totalEarned.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border border-border rounded-2xl" data-testid="total-spent-card">
            <CardContent className="p-5 sm:p-6 text-center">
              <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2"><ArrowUpRight className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1 font-sans">{t('w_spent')}</p>
              <p className="text-2xl sm:text-3xl font-bold text-primary" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totalSpent.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Coin Info */}
        <div className="bg-secondary/50 rounded-2xl p-4 sm:p-5 mb-6 sm:mb-8 flex items-start gap-3">
          <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-sans text-foreground">{t('w_coin_info')}</p>
            <p className="text-xs text-muted-foreground font-sans mt-1">{t('w_expiry')}</p>
          </div>
        </div>

        {/* Transactions */}
        <Card className="border border-border rounded-2xl" data-testid="transaction-history">
          <CardHeader><CardTitle className="text-base sm:text-lg font-sans font-semibold">{t('w_transactions')}</CardTitle></CardHeader>
          <CardContent className="p-0 sm:p-0">
            {transactions.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-10 font-sans">{t('w_no_txns')}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-sans text-xs">Data</TableHead>
                      <TableHead className="font-sans text-xs">Descrizione</TableHead>
                      <TableHead className="font-sans text-xs">Tipo</TableHead>
                      <TableHead className="text-right font-sans text-xs">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx, i) => (
                      <TableRow key={tx.transaction_id || i} data-testid={`transaction-row-${i}`}>
                        <TableCell className="text-xs sm:text-sm font-sans whitespace-nowrap">{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-xs sm:text-sm font-sans">{tx.description}</TableCell>
                        <TableCell><Badge className={`text-[10px] sm:text-xs ${txnBadge(tx.type)}`}>{txnLabel(tx.type)}</Badge></TableCell>
                        <TableCell className={`text-right text-xs sm:text-sm font-semibold font-sans ${isEarn(tx.type) ? 'text-emerald-700' : 'text-primary'}`}>
                          {isEarn(tx.type) ? '+' : '-'}{tx.amount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
